import { Dialog as SheetPrimitive } from 'radix-ui';
import * as React from 'react';

import { Button } from './Button';
import { Icon } from './Icon';
import { cn } from './utils/cn';

/**
 * Slide-in panel anchored to one edge of the viewport. Reach for it when a
 * surface needs more room than a popover but should not commandeer the full
 * screen the way a Dialog does (e.g. side details, file lists, exporters).
 *
 * Compose with {@link SheetTrigger}, {@link SheetContent} (containing
 * {@link SheetHeader}, {@link SheetTitle}, {@link SheetDescription}, and
 * {@link SheetFooter}), and (optionally) {@link SheetClose} for inline close
 * affordances.
 *
 * @example
 * <Sheet>
 *   <SheetTrigger asChild>
 *     <Button variant="outline">View files</Button>
 *   </SheetTrigger>
 *   <SheetContent side="right">
 *     <SheetHeader>
 *       <SheetTitle>Appointment files</SheetTitle>
 *       <SheetDescription>Local files captured during this visit.</SheetDescription>
 *     </SheetHeader>
 *   </SheetContent>
 * </Sheet>
 */
function Sheet({ ...props }: React.ComponentProps<typeof SheetPrimitive.Root>) {
  return <SheetPrimitive.Root data-slot="sheet" {...props} />;
}

/** Toggle that opens the parent {@link Sheet}. Wrap a `Button` (or any clickable) with `asChild`. */
function SheetTrigger({
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Trigger>) {
  return <SheetPrimitive.Trigger data-slot="sheet-trigger" {...props} />;
}

/** Closes the parent {@link Sheet}. Wrap any clickable with `asChild` for a custom close affordance. */
function SheetClose({
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Close>) {
  return <SheetPrimitive.Close data-slot="sheet-close" {...props} />;
}

/** Renders sheet content into a portal. Used internally by {@link SheetContent}. */
function SheetPortal({
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Portal>) {
  return <SheetPrimitive.Portal data-slot="sheet-portal" {...props} />;
}

/** Backdrop behind {@link SheetContent}. Used internally; consumers don't render it directly. */
function SheetOverlay({
  className,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Overlay>) {
  return (
    <SheetPrimitive.Overlay
      data-slot="sheet-overlay"
      className={cn(
        'fixed inset-0 z-50 bg-black/10 duration-100 supports-backdrop-filter:backdrop-blur-xs data-open:animate-in data-open:fade-in-0 data-closed:animate-out data-closed:fade-out-0',
        className,
      )}
      {...props}
    />
  );
}

/**
 * Slide-in panel itself. The `side` prop picks which edge it anchors to;
 * `showCloseButton` toggles the built-in X in the corner; `showOverlay`
 * toggles the dimming backdrop (set to `false` for non-modal surfaces that
 * should coexist with the page rather than block it).
 *
 * Sizing is the consumer's responsibility: pass a width via `className` for
 * `left`/`right` and a height for `top`/`bottom`.
 *
 * Initial focus on open is suppressed by default to keep focus on the
 * trigger; pass an explicit `onOpenAutoFocus` to opt back into the
 * primitive's auto-focus behavior.
 *
 * Outside-click dismissal is suppressed by default when `showOverlay` is
 * `false`, so non-modal panels stay open while the user interacts with the
 * page underneath; pass an explicit `onInteractOutside` to take over.
 */
function SheetContent({
  className,
  children,
  side = 'right',
  showCloseButton = true,
  showOverlay = true,
  onOpenAutoFocus,
  onInteractOutside,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Content> & {
  side?: 'top' | 'right' | 'bottom' | 'left';
  showCloseButton?: boolean;
  showOverlay?: boolean;
}) {
  return (
    <SheetPortal>
      {showOverlay && <SheetOverlay />}
      <SheetPrimitive.Content
        data-slot="sheet-content"
        data-side={side}
        className={cn(
          'fixed z-50 flex flex-col gap-4 bg-popover bg-clip-padding text-sm text-popover-foreground shadow-lg transition duration-200 ease-in-out data-[side=bottom]:inset-x-0 data-[side=bottom]:bottom-0 data-[side=bottom]:border-t data-[side=left]:inset-y-0 data-[side=left]:left-0 data-[side=left]:h-full data-[side=left]:border-r data-[side=right]:inset-y-0 data-[side=right]:right-0 data-[side=right]:h-full data-[side=right]:border-l data-[side=top]:inset-x-0 data-[side=top]:top-0 data-[side=top]:border-b data-open:animate-in data-open:fade-in-0 data-[side=bottom]:data-open:slide-in-from-bottom-10 data-[side=left]:data-open:slide-in-from-left-10 data-[side=right]:data-open:slide-in-from-right-10 data-[side=top]:data-open:slide-in-from-top-10 data-closed:animate-out data-closed:fade-out-0 data-[side=bottom]:data-closed:slide-out-to-bottom-10 data-[side=left]:data-closed:slide-out-to-left-10 data-[side=right]:data-closed:slide-out-to-right-10 data-[side=top]:data-closed:slide-out-to-top-10',
          className,
        )}
        onOpenAutoFocus={onOpenAutoFocus ?? ((event) => event.preventDefault())}
        onInteractOutside={
          onInteractOutside ??
          (showOverlay ? undefined : (event) => event.preventDefault())
        }
        {...props}
      >
        {children}
        {showCloseButton && (
          <SheetPrimitive.Close data-slot="sheet-close" asChild>
            <Button
              aria-label="Close"
              className="absolute top-3 right-3"
              size="sm"
              variant="ghost"
            >
              <Icon name="X" size="auto" />
            </Button>
          </SheetPrimitive.Close>
        )}
      </SheetPrimitive.Content>
    </SheetPortal>
  );
}

/** Header section of a sheet. Holds {@link SheetTitle} and {@link SheetDescription}. */
function SheetHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="sheet-header"
      className={cn('flex flex-col gap-0.5 p-4', className)}
      {...props}
    />
  );
}

/** Footer section of a sheet. Holds primary actions; pinned to the bottom of the panel. */
function SheetFooter({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="sheet-footer"
      className={cn('mt-auto flex flex-col gap-2 p-4', className)}
      {...props}
    />
  );
}

/** Headline of the sheet. Required for accessibility. */
function SheetTitle({
  className,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Title>) {
  return (
    <SheetPrimitive.Title
      data-slot="sheet-title"
      className={cn('text-md font-medium text-foreground', className)}
      {...props}
    />
  );
}

/** Optional supplementary copy below {@link SheetTitle}. */
function SheetDescription({
  className,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Description>) {
  return (
    <SheetPrimitive.Description
      data-slot="sheet-description"
      className={cn('text-sm text-muted-foreground', className)}
      {...props}
    />
  );
}

export {
  Sheet,
  SheetTrigger,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetOverlay,
  SheetPortal,
  SheetTitle,
};
